import React from 'react';
import {Box} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Design, designLabel, hasDesignNameOverride} from '../../../models/Template';

interface DesignTabsProps {
    designs: Design[];
    selectedDesign: number;
    onDesignChange: (index: number) => void;
}

/** Prototype-style format pills for Fill Mode (replaces AvailableDesignGrid on
 *  the phone-first path; the classic editor keeps the grid). Labels by
 *  orientation when the designs mix portrait/landscape (Story vs Post),
 *  otherwise by the trailing segment of each design name. */
const DesignTabs: React.FC<DesignTabsProps> = ({designs, selectedDesign, onDesignChange}) => {
    const {t} = useTranslation();

    const orientationOf = (d: Design) => (d.height > d.width ? 'story' : 'post');
    const mixed = new Set(designs.map(orientationOf)).size > 1;

    const labelFor = (d: Design) => {
        // An admin who typed a name meant that name — show it verbatim rather
        // than running it through the orientation or trailing-segment
        // heuristics, which exist only to make raw Figma labels readable.
        if (hasDesignNameOverride(d)) return designLabel(d);
        if (mixed) {
            return orientationOf(d) === 'story'
                ? t('editor.fillMode.tabStory')
                : t('editor.fillMode.tabPost');
        }
        // Can't disambiguate by orientation — use the name's trailing segment
        // (e.g. "Result-Alef-North-Round1-Post" → "Post").
        const tail = d.name.split('-').pop()?.trim();
        return tail || d.name;
    };

    return (
        <Box sx={{display: 'flex', gap: 1, mb: 1.5}}>
            {designs.map((design, index) => {
                const active = selectedDesign === index;
                return (
                    <Box
                        key={`design-tab-${index}`}
                        onClick={() => onDesignChange(index)}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            height: 38,
                            borderRadius: '999px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            px: 1,
                            fontSize: '0.85rem',
                            background: active ? '#2ea6e6' : 'transparent',
                            color: active ? '#fff' : '#8fb0c8',
                            fontWeight: active ? 700 : 600,
                            border: active ? 'none' : '1.5px solid #26506e',
                        }}
                    >
                        <Box component="span"
                             sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                            {labelFor(design)}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default React.memo(DesignTabs);
